import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where
} from "firebase/firestore";
import { format, startOfYear, endOfYear, eachDayOfInterval } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";
import { db } from "./firebase";
import { useAuth } from "./auth/AuthContext";

export default function ChristmasCounter() {
  const { user } = useAuth();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [availableYears, setAvailableYears] = useState([now.getFullYear()]);
  const [note, setNote] = useState("");
  const [customAt, setCustomAt] = useState("");
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const col = useMemo(() => collection(db, "christmas_hearings"), []);

  // Discover all years with data for this user so the dropdown is accurate
  useEffect(() => {
    if (!user) return;
    const loadYears = async () => {
      try {
        const snap = await getDocs(query(col, where("ownerUid", "==", user.uid)));
        const years = new Set([now.getFullYear()]);
        snap.forEach((d) => {
          const data = d.data();
          const ts = data.at?.toDate ? data.at.toDate() : data.at;
          const y = data.year || (ts instanceof Date ? ts.getFullYear() : null);
          if (y) years.add(y);
        });
        const sorted = Array.from(years).sort((a, b) => b - a);
        setAvailableYears(sorted.length ? sorted : [now.getFullYear()]);
      } catch (err) {
        console.error("[years]", err);
      }
    };
    loadYears();
  }, [user, col, now]);

  // Subscribe to entries for the chosen year
  useEffect(() => {
    if (!user) return;
    setStatus(null);
    const qy = query(
      col,
      where("ownerUid", "==", user.uid),
      where("year", "==", year),
      orderBy("at", "desc")
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const docs = snap.docs.map((d) => {
          const data = d.data();
          const when = data.at?.toDate ? data.at.toDate() : data.at;
          return { id: d.id, ...data, at: when };
        });
        setEntries(docs);
      },
      (err) => {
        console.error("[christmas counter]", err);
        setStatus(`${err.code || "error"}: ${err.message}`);
      }
    );
    return () => unsub();
  }, [user, col, year]);

  const addEntry = async () => {
    if (!user) {
      setStatus("Please sign in to log your hearings.");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const atDate = customAt ? new Date(customAt) : new Date();
      await addDoc(col, {
        ownerUid: user.uid,
        note: note.trim() || null,
        at: serverTimestamp(),
        atClient: atDate.toISOString(),
        year: atDate.getFullYear(),
        createdAt: serverTimestamp()
      });
      setAvailableYears((prev) => {
        const next = new Set(prev);
        next.add(atDate.getFullYear());
        return Array.from(next).sort((a, b) => b - a);
      });
      setNote("");
      setCustomAt("");
      setStatus("Logged!");
    } catch (err) {
      console.error("[add christmas]", err);
      setStatus(`${err.code || "error"}: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Daily totals for the chosen year
  const chartData = useMemo(() => {
    const counts = entries.reduce((acc, entry) => {
      const date = entry.at instanceof Date ? entry.at : new Date(entry.at || entry.atClient);
      if (!date || Number.isNaN(date.getTime())) return acc;
      const key = format(date, "yyyy-MM-dd");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const start = startOfYear(new Date(year, 0, 1));
    const end = endOfYear(start);
    return eachDayOfInterval({ start, end }).map((day) => {
      const key = format(day, "yyyy-MM-dd");
      return {
        key,
        label: format(day, "MMM d"),
        count: counts[key] || 0
      };
    });
  }, [entries, year]);

  const total = entries.length;
  const lastHeard = (() => {
    const first = entries[0];
    const when = first?.at instanceof Date ? first.at : (first ? new Date(first.atClient) : null);
    if (!when || Number.isNaN(when.getTime())) return "—";
    return format(when, "MMM d, yyyy p");
  })();

  if (!user) {
    return (
      <div style={{ maxWidth: 900, margin: "24px auto", padding: 16 }}>
        <h1>Christmas Counter</h1>
        <p>You need to sign in to track your Christmas mentions.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "24px auto", padding: "0 16px" }}>
      <header style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "baseline" }}>
        <div>
          <p style={{ fontSize: 13, margin: 0, color: "#4e342e" }}>Holiday science project</p>
          <h1 style={{ margin: "4px 0 0" }}>Christmas Counter</h1>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={addEntry} disabled={busy} style={{ background: "#c62828", color: "#fff", border: "none", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>
            {busy ? "Logging…" : "Heard “Christmas”"}
          </button>
        </div>
      </header>

      {status && (
        <div style={{ marginTop: 12, padding: 12, background: "#fff8e1", border: "1px solid #fbc02d", borderRadius: 8, color: "#5d4037" }}>
          {status}
        </div>
      )}

      <section style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        <StatCard label={`Times heard in ${year}`} value={total} highlight />
        <StatCard label="Most recent" value={lastHeard} />
        <StatCard label="Notes logged" value={entries.filter((e) => e.note).length} />
      </section>

      <section style={{ marginTop: 20, padding: 16, border: "1px solid #ddd", borderRadius: 12, background: "#fff" }}>
        <h3 style={{ marginTop: 0 }}>Add a note</h3>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 220px" }}>
          <textarea
            placeholder="Where were you? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            style={{ padding: 10, resize: "vertical", borderRadius: 8, border: "1px solid #ccc" }}
          />
          <div style={{ display: "grid", gap: 8 }}>
            <label style={{ fontSize: 12, color: "#444" }}>
              Backfill time (optional)
              <input
                type="datetime-local"
                value={customAt}
                onChange={(e) => setCustomAt(e.target.value)}
                style={{ display: "block", width: "100%", padding: 8, borderRadius: 8, border: "1px solid #ccc", marginTop: 4 }}
              />
            </label>
            <button onClick={addEntry} disabled={busy} style={{ background: "#2e7d32", color: "#fff", border: "none", padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>
              {busy ? "Saving…" : "Save entry"}
            </button>
          </div>
        </div>
      </section>

      <section style={{ marginTop: 20, padding: 16, border: "1px solid #ddd", borderRadius: 12, background: "linear-gradient(135deg,#fff,#f3f6f4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: 999, background: "#d32f2f" }} />
          <h3 style={{ margin: "8px 0" }}>Hearings per day</h3>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="christmasGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef5350" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ef5350" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={30} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={32} />
              <Tooltip content={<TooltipContent />} />
              <Area type="monotone" dataKey="count" stroke="#c62828" fill="url(#christmasGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section style={{ marginTop: 20, padding: 16, border: "1px solid #ddd", borderRadius: 12, background: "#fff" }}>
        <h3 style={{ marginTop: 0 }}>Recent logs</h3>
        {entries.length === 0 ? (
          <p style={{ color: "#666" }}>No entries yet.</p>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {entries.slice(0, 25).map((entry) => (
              <div key={entry.id} style={{ padding: 10, border: "1px solid #eee", borderRadius: 8, background: "#fafafa", display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {(() => {
                      const when = entry.at instanceof Date ? entry.at : new Date(entry.atClient);
                      return when && !Number.isNaN(when.getTime()) ? format(when, "PPpp") : "Pending…";
                    })()}
                  </div>
                  {entry.note && <div style={{ color: "#444" }}>{entry.note}</div>}
                </div>
                <div style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>year {entry.year}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div style={{ padding: 14, borderRadius: 12, border: "1px solid #ddd", background: highlight ? "linear-gradient(135deg,#ffebee,#fff)" : "#fff", boxShadow: highlight ? "0 8px 20px rgba(198,40,40,0.15)" : "none" }}>
      <div style={{ fontSize: 12, color: "#555" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#b71c1c" }}>{value}</div>
    </div>
  );
}

function TooltipContent({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #ddd", padding: 8, borderRadius: 8 }}>
      <div style={{ fontWeight: 700 }}>{label}</div>
      <div>{payload[0].value} heard</div>
    </div>
  );
}
